/**
 * CajasPage - Gestión de cajas de efectivo
 * Flujo del cajero: 1 caja por día por usuario (Abrir → Movimientos → Cerrar),
 * con Resumen de Caja en PDF (descargar/imprimir).
 * Administrador/contador: edición de cajas abiertas y supervisión de saldos.
 */
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Lock,
  Paid,
  PictureAsPdf,
  Print,
  PointOfSale,
  AccountBalanceWallet,
} from '@mui/icons-material';
import {
  PageHeader,
  LoadingSpinner,
  DataTable,
} from '../components/common';
import DenominacionesInput, {
  calcularTotalCentavos,
  construirDetalle,
} from '../components/cajas/DenominacionesInput';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import * as cajaService from '../services/cajaService';
import * as catalogoService from '../services/catalogoService';
import { formatCurrency } from '../utils/formatters';

// Esquema del cajero: apertura/edición de su caja del día
const cajaCajeroSchema = yup.object().shape({
  valor_inicial: yup
    .number()
    .typeError('El valor inicial debe ser un número')
    .min(0, 'El valor inicial debe ser 0 o mayor')
    .required('Debe registrar el valor inicial de la caja'),
  descripcion: yup.string(),
});

// Esquema de administrador/contador: edición general
const cajaAdminSchema = yup.object().shape({
  nombre: yup.string().required('El nombre de la caja es requerido'),
  descripcion: yup.string(),
  responsable_id: yup.string(),
  activa: yup.boolean(),
});

const CajasPage = () => {
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cajas, setCajas] = useState([]);
  const [miCaja, setMiCaja] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCaja, setEditingCaja] = useState(null);
  const [desglose, setDesglose] = useState(null);
  const [cajaACerrar, setCajaACerrar] = useState(null);
  const [cerrando, setCerrando] = useState(false);
  // Registro de Efectivo al cierre: catálogo USD y cantidades contadas
  const [catalogo, setCatalogo] = useState([]);
  const [cantidadesCierre, setCantidadesCierre] = useState({});

  const esCajero = user?.rol === 'cajero';
  const esAdmin = user?.rol === 'administrador';
  const puedeEditar = ['administrador', 'contador'].includes(user?.rol);
  const puedeCerrar = ['administrador', 'contador'].includes(user?.rol);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(esCajero ? cajaCajeroSchema : cajaAdminSchema),
    defaultValues: esCajero
      ? { valor_inicial: '', descripcion: '' }
      : { nombre: '', descripcion: '', responsable_id: '', activa: true },
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [cajasData, miCajaData, usuariosData] = await Promise.all([
        cajaService.getCajas(),
        // Caja del día del usuario: decide entre "Abrir" y "Editar/Cerrar"
        esCajero ? cajaService.getMiCajaHoy().catch(() => null) : Promise.resolve(null),
        // El listado de usuarios es solo para administradores
        esAdmin ? catalogoService.getUsuarios({ activo: true }).catch(() => []) : Promise.resolve([]),
      ]);
      setCajas(cajasData || []);
      setMiCaja(miCajaData || null);
      setUsuarios(usuariosData || []);
    } catch (error) {
      console.error('Error al cargar cajas:', error);
      showError('Error al cargar las cajas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Apertura de caja del día (cajero sin caja registrada hoy)
  const handleOpenCreate = () => {
    setEditingCaja(null);
    reset({ valor_inicial: '', descripcion: '' });
    setOpenDialog(true);
  };

  const handleOpenEdit = (caja) => {
    if (caja.estado === 'cerrada') {
      showError('La caja está cerrada y no se puede modificar');
      return;
    }
    if (esCajero && caja.responsable_id !== user?.id) {
      showError('Solo puede editar la caja asociada a su usuario');
      return;
    }
    setEditingCaja(caja);
    if (esCajero) {
      reset({
        valor_inicial: parseFloat(caja.valor_inicial || 0),
        descripcion: caja.descripcion || '',
      });
    } else {
      reset({
        nombre: caja.nombre,
        descripcion: caja.descripcion || '',
        responsable_id: caja.responsable_id || '',
        activa: caja.activa !== false,
      });
    }
    setOpenDialog(true);
  };

  // Ver desglose de monedas/billetes de una caja
  const handleVerDesglose = async (caja) => {
    try {
      const data = await cajaService.getCajaDenominaciones(caja.id);
      setDesglose(data);
    } catch (error) {
      showError('Error al cargar el desglose de la caja');
    }
  };

  // Resumen de Caja en PDF (A4)
  const handleDescargarResumen = async (caja) => {
    try {
      await cajaService.downloadResumenCaja(
        caja.id,
        `resumen_caja_${caja.fecha_apertura || caja.id}.pdf`
      );
    } catch (error) {
      showError(error.response?.data?.error || 'Error al descargar el resumen de caja');
    }
  };

  const handleImprimirResumen = async (caja) => {
    try {
      await cajaService.imprimirResumenCaja(caja.id);
    } catch (error) {
      showError(error.response?.data?.error || 'Error al imprimir el resumen de caja');
    }
  };

  // Abrir el diálogo de cierre con el Registro de Efectivo
  const handleAbrirCierre = async (caja) => {
    try {
      if (catalogo.length === 0) {
        const data = await cajaService.getCatalogoDenominaciones();
        setCatalogo(data || []);
      }
      setCantidadesCierre({});
      setCajaACerrar(caja);
    } catch (error) {
      showError('Error al cargar el catálogo de denominaciones');
    }
  };

  // Cierre de caja: envía el conteo físico por denominación
  const handleConfirmarCierre = async () => {
    try {
      setCerrando(true);
      const detalle = construirDetalle(catalogo, cantidadesCierre);
      const resp = await cajaService.cerrarCaja(cajaACerrar.id, detalle);
      showSuccess(resp?.message || 'Caja cerrada. Ya no admite ediciones ni movimientos.');
      setCajaACerrar(null);
      loadData();
    } catch (error) {
      showError(error.response?.data?.error || 'Error al cerrar la caja');
    } finally {
      setCerrando(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCaja(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      if (editingCaja) {
        const payload = esCajero
          ? { valor_inicial: data.valor_inicial, descripcion: data.descripcion }
          : {
              nombre: data.nombre,
              descripcion: data.descripcion,
              responsable_id: data.responsable_id || null,
              activa: data.activa,
            };
        await cajaService.updateCaja(editingCaja.id, payload);
        showSuccess('Caja actualizada correctamente');
      } else {
        // Apertura: la caja queda asociada al cajero, 1 por día por usuario
        await cajaService.createCaja({
          valor_inicial: data.valor_inicial,
          descripcion: data.descripcion,
        });
        showSuccess('Caja del día abierta correctamente. Ya puede registrar ingresos y egresos.');
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error al guardar caja:', error);
      showError(error.response?.data?.error || 'Error al guardar la caja');
    }
  };

  // Total de efectivo en cajas activas
  const totalEfectivo = cajas
    .filter((c) => c.activa)
    .reduce((sum, c) => sum + parseFloat(c.saldo_actual || 0), 0);

  // El cajero puede cerrar/editar solo su propia caja abierta
  const puedeCerrarCaja = (caja) =>
    caja.estado === 'abierta' &&
    (puedeCerrar || (esCajero && caja.responsable_id === user?.id));

  const columns = [
    {
      field: 'nombre',
      headerName: 'Caja',
    },
    {
      field: 'responsable',
      headerName: 'Responsable',
      renderCell: (row) => row.responsable?.nombre || '-',
    },
    {
      field: 'fecha_apertura',
      headerName: 'Fecha',
      renderCell: (row) => row.fecha_apertura || '-',
    },
    {
      field: 'valor_inicial',
      headerName: 'Valor Inicial',
      renderCell: (row) => formatCurrency(parseFloat(row.valor_inicial || 0)),
    },
    {
      field: 'saldo_actual',
      headerName: 'Saldo Actual',
      renderCell: (row) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: parseFloat(row.saldo_actual) > 0 ? 'success.main' : 'text.secondary' }}
        >
          {formatCurrency(parseFloat(row.saldo_actual))}
        </Typography>
      ),
    },
    {
      field: 'valor_cierre',
      headerName: 'Efectivo Entregado',
      renderCell: (row) =>
        row.valor_cierre !== null && row.valor_cierre !== undefined
          ? formatCurrency(parseFloat(row.valor_cierre))
          : '-',
    },
    {
      field: 'estado',
      headerName: 'Estado',
      renderCell: (row) => (
        <Chip
          label={row.estado === 'abierta' ? 'Abierta' : 'Cerrada'}
          color={row.estado === 'abierta' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
  ];

  if (loading) {
    return <LoadingSpinner message="Cargando cajas..." />;
  }

  // Acciones del encabezado según el estado de la caja del día del cajero
  const headerActions = [];
  if (esCajero) {
    if (!miCaja) {
      headerActions.push({
        label: 'Abrir Caja',
        icon: AddIcon,
        onClick: handleOpenCreate,
        variant: 'contained',
      });
    } else if (miCaja.estado === 'abierta') {
      headerActions.push({
        label: 'Editar Mi Caja',
        icon: EditIcon,
        onClick: () => handleOpenEdit(miCaja),
        variant: 'contained',
      });
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Cajas de Efectivo"
        breadcrumbs={[{ label: 'Cajas' }]}
        actions={headerActions}
      />

      {esCajero && miCaja?.estado === 'cerrada' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Su caja del día <strong>{miCaja.fecha_apertura}</strong> está <strong>cerrada</strong> y
          ya no admite modificaciones. Puede descargar o imprimir el Resumen de Caja desde las
          acciones de la tabla.
        </Alert>
      )}

      {/* Tarjetas de resumen */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AccountBalanceWallet color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Efectivo (cajas activas)
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {formatCurrency(totalEfectivo)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PointOfSale color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Cajas Abiertas
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {cajas.filter((c) => c.estado === 'abierta').length} de {cajas.length}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabla de cajas */}
      <DataTable
        columns={columns}
        data={cajas}
        onEdit={puedeEditar || esCajero ? handleOpenEdit : undefined}
        searchPlaceholder="Buscar por nombre..."
        emptyMessage="No hay cajas registradas"
        renderCustomActions={(row) => (
          <>
            <Tooltip title="Ver desglose de monedas y billetes">
              <IconButton size="small" color="success" onClick={() => handleVerDesglose(row)}>
                <Paid fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Descargar Resumen de Caja (PDF)">
              <IconButton size="small" color="error" onClick={() => handleDescargarResumen(row)}>
                <PictureAsPdf fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Imprimir Resumen de Caja">
              <IconButton size="small" color="primary" onClick={() => handleImprimirResumen(row)}>
                <Print fontSize="small" />
              </IconButton>
            </Tooltip>
            {puedeCerrarCaja(row) && (
              <Tooltip title="Cerrar caja: registrar el efectivo que entrega">
                <IconButton size="small" color="warning" onClick={() => handleAbrirCierre(row)}>
                  <Lock fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      />

      {/* Diálogo abrir/editar caja */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingCaja ? 'Editar Caja' : 'Abrir Caja del Día'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {esCajero ? (
                <>
                  <TextField
                    {...register('valor_inicial')}
                    label="Valor inicial de la caja"
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    fullWidth
                    autoFocus
                    error={!!errors.valor_inicial}
                    helperText={errors.valor_inicial?.message}
                  />
                  <TextField
                    {...register('descripcion')}
                    label="Descripción (opcional)"
                    fullWidth
                    multiline
                    rows={2}
                    error={!!errors.descripcion}
                    helperText={errors.descripcion?.message}
                  />
                  {!editingCaja && (
                    <Alert severity="info">
                      La caja quedará <strong>asociada a su usuario</strong> ({user?.nombre}) con
                      la fecha de hoy. Solo se permite <strong>una caja por día</strong>. El valor
                      inicial es el efectivo con el que abre; los ingresos y egresos se registran
                      luego en <strong>Movimientos de Caja</strong>.
                    </Alert>
                  )}
                  {editingCaja && (
                    <Alert severity="warning">
                      Al cambiar el valor inicial, el saldo de la caja se ajusta por la
                      diferencia. Solo puede editar mientras la caja esté <strong>Abierta</strong>.
                    </Alert>
                  )}
                </>
              ) : (
                <>
                  <TextField
                    {...register('nombre')}
                    label="Nombre de la caja"
                    fullWidth
                    autoFocus
                    error={!!errors.nombre}
                    helperText={errors.nombre?.message}
                  />
                  <TextField
                    {...register('descripcion')}
                    label="Descripción"
                    fullWidth
                    multiline
                    rows={2}
                    error={!!errors.descripcion}
                    helperText={errors.descripcion?.message}
                  />
                  {esAdmin && (
                    <Controller
                      name="responsable_id"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} select label="Usuario responsable (cajero asociado)" fullWidth>
                          <MenuItem value="">Sin responsable asignado</MenuItem>
                          {usuarios.map((usuario) => (
                            <MenuItem key={usuario.id} value={usuario.id}>
                              {usuario.nombre} ({usuario.rol})
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...register('activa')}
                        defaultChecked={editingCaja?.activa !== false}
                      />
                    }
                    label="Caja activa"
                  />
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingCaja ? 'Actualizar' : 'Abrir Caja'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo de cierre: Registro de Efectivo por denominación */}
      <Dialog open={!!cajaACerrar} onClose={() => setCajaACerrar(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Cerrar Caja — Registro de Efectivo</DialogTitle>
        <DialogContent>
          {cajaACerrar && (() => {
            const saldoSistema = parseFloat(cajaACerrar.saldo_actual || 0);
            const totalContado = calcularTotalCentavos(catalogo, cantidadesCierre) / 100;
            const diferencia = Math.round((totalContado - saldoSistema) * 100) / 100;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Alert severity="info">
                  Detalle el dinero <strong>físico</strong> con el que cierra{' '}
                  <strong>{cajaACerrar.nombre}</strong>, contado por billetes y monedas
                  (1¢, 5¢, … $1, $5, $10, $20, $50, $100). Este es el valor que entregará.
                </Alert>

                <DenominacionesInput
                  catalogo={catalogo}
                  cantidades={cantidadesCierre}
                  onChange={setCantidadesCierre}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2">
                    Saldo del sistema: <strong>{formatCurrency(saldoSistema)}</strong>
                  </Typography>
                  <Chip
                    label={
                      diferencia === 0
                        ? 'Cuadrado'
                        : `${diferencia > 0 ? 'Sobrante' : 'Faltante'}: ${formatCurrency(Math.abs(diferencia))}`
                    }
                    color={diferencia === 0 ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>

                {diferencia !== 0 && totalContado > 0 && (
                  <Alert severity="warning">
                    El efectivo contado ({formatCurrency(totalContado)}) no cuadra con el saldo
                    del sistema ({formatCurrency(saldoSistema)}). Puede cerrar de todas formas;
                    la diferencia quedará registrada en la auditoría y en el Resumen de Caja.
                  </Alert>
                )}

                <Alert severity="warning">
                  Una caja cerrada <strong>no se puede volver a editar</strong> ni recibir
                  movimientos. Verifique que registró todos los ingresos y egresos del día.
                </Alert>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCajaACerrar(null)}>Cancelar</Button>
          <Button
            onClick={handleConfirmarCierre}
            variant="contained"
            color="warning"
            disabled={
              cerrando ||
              (parseFloat(cajaACerrar?.saldo_actual || 0) > 0 &&
                calcularTotalCentavos(catalogo, cantidadesCierre) === 0)
            }
          >
            {cerrando ? 'Cerrando...' : 'Registrar Efectivo y Cerrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de desglose de denominaciones */}
      <Dialog open={!!desglose} onClose={() => setDesglose(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Desglose de Efectivo — {desglose?.caja}</DialogTitle>
        <DialogContent>
          {desglose && (
            <Box>
              {!desglose.cuadra && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  El desglose detallado ({formatCurrency(desglose.total_desglose)}) difiere del
                  saldo de la caja ({formatCurrency(desglose.saldo_actual)}). Los egresos/gastos
                  no descuentan denominaciones (solo el saldo); use un <strong>ajuste</strong> al
                  hacer arqueo de caja para actualizar el conteo de monedas y billetes.
                </Alert>
              )}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Denominación</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Cantidad</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {desglose.denominaciones.map((d) => (
                      <TableRow key={`${d.tipo}-${d.denominacion}`} hover>
                        <TableCell>{d.etiqueta}</TableCell>
                        <TableCell>{d.tipo === 'moneda' ? 'Moneda' : 'Billete'}</TableCell>
                        <TableCell align="center">{d.cantidad}</TableCell>
                        <TableCell align="right">
                          {d.cantidad > 0 ? formatCurrency(d.subtotal) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>
                        TOTAL
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(desglose.total_desglose)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDesglose(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CajasPage;
